import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, useOutletContext } from "react-router-dom"
import { AlertType } from "./types"

import { setCaseRAtype } from "@/UDTreat/udTreatSlice"

import { UButton, UText } from "../../../components"
import { Box, Grid } from "../../../components/mui.components"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { getBundleByCaseId } from "../../../core/app/slices/bundle/bundleThunkApi"
import { resetCasedata } from "../../../core/app/slices/case/caseSlice"
import {
  getCaseByPatient,
  getCaseFilesByCaseId,
  updateCaseStatus,
} from "../../../core/app/slices/case/caseThunkApi"
import { resetCase } from "../../../core/app/slices/clinical/clinicalSlice"
import { setRouteFrom } from "../../../core/app/slices/ConfigSlice"
import { getOrdersByCaseId } from "../../../core/app/slices/order/orderThunkApi"
import { addNewPatient } from "../../../core/app/slices/patients"
import { setData } from "../../../core/app/slices/patients/patientSlice"
import { resetPhotoLists } from "../../../core/app/slices/records/photograph/photographSlice"
import { resetXrays } from "../../../core/app/slices/records/xrays/xraysSlice"
import { RootState } from "../../../core/app/store"
import { ICase } from "../../../core/model/interface/ICase"
import { UI_PERMISSION } from "../../../core/utils/access/permissionConstants"
import { useMyNavigation } from "../../../hooks/useMyNavigation"
import usePermissions from "../../../hooks/userPermission"
import { CASE_STATUS } from "../config/status.config"
import { downloadZipFile } from "../PatientList/util/commonUtil"
import { navigateTo } from "../utils"
import { useTabRef } from "@/routes/TabProvider"
import { openCaseTab } from "@/modules/Patient/config/tab.config"

import CaseActionPopup from "./components/CaseActionPopup"
import CaseOverview from "./components/CaseOverview"
import {
  resetPrescriptionSchema,
  resetSchema,
} from "@/core/app/slices/uassist/formSlice"

const RenderByCaseStatus: FC = () => {
  const { hasAny } = usePermissions()
  const { t } = useTranslation()
  const { patientId, orgId } = useParams()
  const dispatch = useAppDispatch()
  const { caseList, caseListFetching } = useAppSelector(
    (state: RootState) => state.caseService,
  )
  const { patientData, isExistingPatientCheckModal } = useAppSelector(
    (state: RootState) => state.patientService,
  )
  const { user } = useAppSelector((state: RootState) => state.userService)
  const navigate = useMyNavigation()
  const tabRefs = useTabRef()
  const [openTabExceededModal, setOpenTabExceededModal] = useState(false)


  /* ****
   * Get alert state passed from Parent(through Outlet)
   * If not needed in parent and is not passed, 
   * create and use local useState
   */
  const outletContextProps = useOutletContext<{
    isAlertOpen: AlertType
    setIsAlertOpen: (type: AlertType) => void
  }>()

  const localAlertState = useState<AlertType>("")

  const isAlertOpen = outletContextProps?.isAlertOpen || localAlertState[0]
  const setIsAlertOpen = outletContextProps?.setIsAlertOpen || localAlertState[1]
  // ****


  const [caseId, setCaseId] = useState("")
  const [assignedTo, setAssignedTo] = useState<number | null>(null)
  const [phaseId, setPhaseId] = useState<number | null>(null)

  useEffect(() => {
    dispatch(resetCasedata())
    dispatch(setRouteFrom(""))
  }, [dispatch])

  useEffect(() => {
    if (caseList.length > 0 && !caseList[0].assigned_to) {
      setIsAlertOpen("OPEN_ASSIGN_MODEL")
    }
  }, [caseList])

  const checkforActiveCase = () => {
    const payload = {
      firstname: patientData.firstName,
      lastname: patientData.lastName,
      dob: patientData.dob,
      patient_id: patientData.id,
      assigned_to: caseList[0]?.assigned_to || null,
    }
    dispatch(addNewPatient(payload)).then((res) => {
      if (res.payload?.data?.status === "Success") {
        dispatch(setData({ newPatientAPI: "idle" }))
        //refresh the same page
        window.location.href = `/overview/patient/${patientId}`
      } else {
        setIsAlertOpen("addNewCase")
      }
    })
  }

  const executeAction = (action: string, caseObj: ICase) => {
    setCaseId(caseObj.id.toString())
    setAssignedTo(caseObj.assigned_to)
    setPhaseId(caseObj.phase)
    dispatch(setRouteFrom("PATIENT_OVERVIEW_MODULE"))
    const udesign_json = JSON.parse(caseObj.udesign_json)
    const isretainerOnly = udesign_json?.UpperType === "R" && udesign_json?.LowerType === "R"
    const pId = patientId || patientData.id

    // Helper function to open case tab with common parameters
    const openTab = (route: string) => {
      openCaseTab({
        route,
        tabRefs,
        setOpenTabExceededModal,
        orgId,
        isAllowToViewAllOrg: user?.isAllowToViewAllOrg,
      })
    }

    // Helper function to reset form data
    const resetData = () => {
      dispatch(resetXrays())
      dispatch(resetPhotoLists())
      dispatch(resetCase())
      dispatch(resetPrescriptionSchema())
      dispatch(resetSchema())
    }

    // Helper function to reset form data with RA type
    const resetFormData = () => {
      dispatch(setCaseRAtype(""))
      resetData()
    }

    // Helper function to download case files
    const downloadCaseFiles = (patientId: number, caseId: number) => {
      dispatch(
        getCaseFilesByCaseId({ patientId: patientId, caseId: caseId }),
      ).then((res) => {
        downloadZipFile(res.payload.data, caseId)
      })
    }

    // Helper function to update case status and refresh data
    const updateStatus = (caseDisposition: string) => {
      dispatch(
        updateCaseStatus({
          patientId: pId.toString(),
          caseId: caseObj.id.toString(),
          payload: { case_disposition: caseDisposition },
        }),
      ).then((res) => {
        if (res?.payload?.response_code === 200) {
          dispatch(getCaseByPatient({ patientId: pId }))
          dispatch(getOrdersByCaseId(caseObj.id))
          dispatch(getBundleByCaseId(caseObj.id))
        }
      })
    }

    switch (action) {
      case "OPEN_RECORDS":
        openTab(`/patients/choosetype/patient/${pId}/case/${caseObj.id}?from=overview`)
        break

      case "OPEN_PRESCRIPTION_FORM":
        resetFormData()
        if(caseObj.isRefinementReady){
          openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview&doRefinement=true`)
        } else {
          openTab(`/records/patient/${pId}/case/${caseObj.id}/rxForm/prescription?from=overview`)
        }
        break

      case "OPEN_SUMMARY":
        if(caseObj.isRefinementReady){
          openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview&doRefinement=true`)
        } else {
          const summaryRoute = 'summary'
          openTab(`/records/patient/${pId}/case/${caseObj.id}/rxForm/${summaryRoute}?from=overview`)
        }
        break

      case "OPEN_RETAINER_FORM":
        navigateTo("RETAINER", navigate, pId, caseObj.id)
        break

      case "START_REFINEMENT":
        resetData()
        openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview&doRefinement=true`)
        break

      case "OPEN_ARCHIVE_PATIENT_MODEL":
      case "OPEN_CASE_ARCHIVE_MODEL":
      case "OPEN_CANCEL_MODEL":
      case "OPEN_CASE_UNARCHIVE_MODEL":
      case "UNLOCK_CASE":
      case "DEACTIVATE_CASE":
      case "CHANGE_WORKFLOW":
        setIsAlertOpen(action)
        break

      case "ADD_NEW_CASE_MODEL":
        dispatch(setRouteFrom(""))
        checkforActiveCase()
        break

      case "OPEN_TREAT_PAGE":
        openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview`)
        break

      case "UPDATE_STATUS_DELIVERED_TO_IN_PROGRESS":
        updateStatus(CASE_STATUS.TREATMENT_IN_PROGRESS)
        break

      case "UPDATE_STATUS_IN_PROGRESS_TO_COMPLETE":
        updateStatus(CASE_STATUS.TREATMENT_COMPLETE)
        break

      case "DOWNLOAD_FILES":
        downloadCaseFiles(Number(pId), caseObj?.id)
        break

      default:
        console.log(action, "..action")
    }
  }

  return (
    <>
      {!!caseList.length &&
        caseList?.map((caseObj: ICase, index: number) => (
          <Grid item mb={5} key={index}>
            <CaseOverview
              index={index}
              caseObj={caseObj}
              setIsAlertOpen={setIsAlertOpen}
              executeAction={executeAction}
            />
          </Grid>
        ))}

      {!caseList.length && caseListFetching === "succeeded" && (
        <Grid item mb={5}>
          {!isExistingPatientCheckModal && (
            <Grid
              item
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"flex-end"}
              sx={{ mb: 3 }}
            >
              {hasAny(UI_PERMISSION.UI_TXPLANS_CREATE) && (
                <UButton
                  variant="text"
                  disabled={patientData?.status === "ARCHIVED"}
                  onClickHandler={() => {
                    checkforActiveCase()
                  }}
                  sxProp={{
                    height: 30,
                    width: 113,
                    padding: "5px",
                    fontSize: "13px",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  btnText={t("overview.buttons.addNewCaseBtn")}
                />
              )}
              {hasAny(UI_PERMISSION.UI_TXPLANS_CLINICALVIEW) && (
                <UButton
                  variant="contained"
                  disabled={
                    patientData?.status === "ARCHIVED" || !caseList.length
                  }
                  sxProp={{
                    height: 30,
                    width: 95,
                    padding: "4px 10px",
                    fontSize: "13px",
                    ml: 2,
                  }}
                  btnText={t("overview.buttons.openCaseBtn")}
                  onClickHandler={() => {
                    console.log("NO Action Required for")
                  }}
                />
              )}
            </Grid>
          )}
          <Grid item>
            <Box
              sx={{
                padding: "24px 40px",
                minHeight: "220px",
                backgroundColor: "background.default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "16px",
                mb: 3,
              }}
            >
              {caseListFetching === "succeeded" && (
                <Grid
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <UText variant="body1" sxProp={{ color: "text.secondary" }}>
                    {"Please add a new case"}
                  </UText>
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
      <CaseActionPopup
        isAlertOpen={isAlertOpen}
        setIsAlertOpen={setIsAlertOpen}
        caseId={caseId}
        assignedTo={assignedTo}
        phaseId={phaseId}
      />
    </>
  )
}

export default RenderByCaseStatus
